import {
  SearchChemistryGroups,
  SearchProductGroups,
  SearchSteelGrades,
} from "contexts/search/context";
import { usePeriods, useTargetBaskets } from "hooks/search";
import {
  useBaskets,
  useChemistryGroups,
  useProductGroups,
  useSteelGrades,
} from "contexts/search/SearchContextProvider";
import { usePeriodIndex } from "hooks/periodIndex";
import { Loaded, liftLoadedState, mapLoaded } from "models/loaded";
import { useMemo } from "react";
import { productionPlanToSteelGradeIds } from "src/utils/productionPlanToSteelGradeIds";
import {
  SearchPeriodProductionPlan,
  TargetBasketItem,
} from "store/api/generatedApi";

export interface TargetBasketDependencies {
  productionPlans: SearchPeriodProductionPlan[];
  productGroups: SearchProductGroups;
  chemistryGroups: SearchChemistryGroups;
  targetBaskets: TargetBasketItem[];
  steelGrades: SearchSteelGrades;
  maxBaskets: number;
}

export const useTargetBasketDependencies =
  (): Loaded<TargetBasketDependencies> => {
    const [periods] = usePeriods();
    const periodTab = usePeriodIndex();
    const productGroups = useProductGroups();
    const chemistryGroups = useChemistryGroups();
    const [targetBaskets] = useTargetBaskets(periodTab);
    const steelGrades = useSteelGrades();
    const baskets = useBaskets();

    const productionPlans = mapLoaded(periods, (periods) =>
      periods.map((period) => period.production_plan)
    );

    return liftLoadedState({
      productionPlans,
      productGroups,
      chemistryGroups,
      targetBaskets,
      steelGrades,
      maxBaskets: mapLoaded(baskets, (b) => b.length),
    });
  };

/**
 * Work out which chemistry group IDs are present in the production plan.
 */
export const usePresentChemistryGroups = (
  productionPlans: SearchPeriodProductionPlan[],
  productGroups: SearchProductGroups,
  chemistryGroups: SearchChemistryGroups
): number[] =>
  useMemo(() => {
    const steelGrades = new Set(
      productionPlans.flatMap((productionPlan) =>
        productionPlanToSteelGradeIds(productionPlan, productGroups)
      )
    );

    return chemistryGroups.byIndex
      .filter((group) =>
        group.steel_grades.some(({ id }) => steelGrades.has(id))
      )
      .map((group) => group.id);
  }, [productionPlans, productGroups, chemistryGroups]);
